import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** Customer-facing API: cookie session (httpOnly) — relative base so requests stay same-origin on any storefront domain (custom domain / preview). */
export const storeApi = axios.create({ baseURL: '/api/store', timeout: 60000, withCredentials: true });

export const idr = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
export const errText = (e, fallback = 'Terjadi kesalahan') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(', ');
  return d.msg || String(d);
};

export const ORDER_STATUS = {
  pending_payment: { label: 'Menunggu pembayaran', cls: 'bg-amber-100 text-amber-900' },
  paid: { label: 'Dibayar', cls: 'bg-emerald-100 text-emerald-900' },
  processing: { label: 'Diproses', cls: 'bg-sky-100 text-sky-900' },
  shipped: { label: 'Dikirim', cls: 'bg-indigo-100 text-indigo-900' },
  delivered: { label: 'Selesai', cls: 'bg-stone-200 text-stone-800' },
  cancelled: { label: 'Dibatalkan', cls: 'bg-rose-100 text-rose-900' },
};

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const cartKey = (slug) => `pillot_cart_${slug}`;

export const StoreProvider = ({ slug, children }) => {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(0);
  const [customer, setCustomer] = useState(undefined); // undefined = checking
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem(cartKey(slug)) || '[]'); } catch { return []; } });

  useEffect(() => { localStorage.setItem(cartKey(slug), JSON.stringify(cart)); }, [cart, slug]);

  const loadStore = useCallback(async (q = '') => {
    try { const { data } = await storeApi.get(`/${slug}`, { params: { q } }); setStore(data.store); setProducts(data.products); setCategories(data.categories || []); setError(null); return data; }
    catch (e) { setError(errText(e, 'Toko tidak ditemukan')); setErrorStatus(e?.response?.status || 0); return null; }
  }, [slug]);
  const loadMe = useCallback(async () => {
    try { const { data } = await storeApi.get(`/${slug}/auth/me`); setCustomer(data.customer); } catch { setCustomer(null); }
  }, [slug]);
  useEffect(() => { loadStore(); loadMe(); }, [loadStore, loadMe]);

  const add = (p, qty = 1) => setCart((c) => {
    const i = c.findIndex((x) => x.product_id === p.id);
    if (i >= 0) { const n = [...c]; n[i] = { ...n[i], qty: n[i].qty + qty }; return n; }
    return [...c, { product_id: p.id, name: p.name, price: p.price, image_url: p.image_url, weight_grams: p.weight_grams, stock: p.stock, qty }];
  });
  const setQty = (id, qty) => setCart((c) => qty <= 0 ? c.filter((x) => x.product_id !== id) : c.map((x) => (x.product_id === id ? { ...x, qty } : x)));
  const clearCart = () => setCart([]);
  const subtotal = useMemo(() => cart.reduce((a, x) => a + x.price * x.qty, 0), [cart]);
  const count = useMemo(() => cart.reduce((a, x) => a + x.qty, 0), [cart]);

  const logout = async () => { try { await storeApi.post(`/${slug}/auth/logout`); } finally { setCustomer(null); } };

  return <StoreContext.Provider value={{ slug, store, products, categories, error, errorStatus, customer, setCustomer, loadStore, loadMe, cart, add, setQty, clearCart, subtotal, count, logout }}>{children}</StoreContext.Provider>;
};
